/**
 * maps.ts
 *
 * Flat lookup maps derived from the nested route tree.
 *
 * The tree in routes.ts is hierarchical (mirrors the URL structure).
 * These maps give consumers a simple, stable key → value lookup for the two
 * things most code needs: the URL segment and the absolute path.
 * Adding a new route:
 *   1. Add the new entry to `FlatRouteKey`.
 *   2. Add a row (pointing into the route tree) in both `routeSegments` and
 *      `routePaths`.  Compiler errors will guide you if you miss one.
 */

import { routes } from '../..';

export const getRoutesSafely = (): typeof routes | undefined => {
  try {
    return routes;
  } catch {
    return undefined;
  }
};

const safeRoutes = getRoutesSafely();

const fallbackRouteSegments = {
  root: '',
  engagementLetterDatabase: 'engagement-letter-database',
  engagementLetterDatabaseHome: '',
  engagementLetterDatabaseClient: 'client/:clientId',
  engagementLetterDatabaseWaiver: 'waiver/:waiverId',
  app: 'app',
} as const;

const fallbackRoutePaths = {
  root: '/',
  engagementLetterDatabase: '/engagement-letter-database',
  engagementLetterDatabaseHome: '/engagement-letter-database',
  engagementLetterDatabaseClient: '/engagement-letter-database/client/:clientId',
  engagementLetterDatabaseWaiver: '/engagement-letter-database/waiver/:waiverId',
  app: '/app',
} as const;

// ─── Exhaustive key union ─────────────────────────────────────────────────────
//
// Every leaf and branch in the route tree has an entry here.
// TypeScript will tell you at compile time if routeSegments / routePaths
// are missing a key.

export type FlatRouteKey =
  | 'root'
  | 'engagementLetterDatabase'
  | 'engagementLetterDatabaseHome'
  | 'engagementLetterDatabaseClient'
  | 'engagementLetterDatabaseWaiver'
  | 'app';

// ─── Segment map ─────────────────────────────────────────────────────────────
//
// Maps a FlatRouteKey → the raw URL segment string used in <Route path=...>.
// e.g. routeSegments.engagementLetterDatabaseClient === 'client/:clientId'

export const routeSegments: Record<FlatRouteKey, string> = {
  root: safeRoutes?.root?.segment ?? fallbackRouteSegments.root,
  engagementLetterDatabase:
    safeRoutes?.engagementLetterDatabase?.segment ?? fallbackRouteSegments.engagementLetterDatabase,
  engagementLetterDatabaseHome:
    safeRoutes?.engagementLetterDatabase?.home?.segment ?? fallbackRouteSegments.engagementLetterDatabaseHome,
  engagementLetterDatabaseClient:
    safeRoutes?.engagementLetterDatabase?.client?.segment ?? fallbackRouteSegments.engagementLetterDatabaseClient,
  engagementLetterDatabaseWaiver:
    safeRoutes?.engagementLetterDatabase?.waiver?.segment ?? fallbackRouteSegments.engagementLetterDatabaseWaiver,
  app: safeRoutes?.app?.segment ?? fallbackRouteSegments.app,
};

// ─── Path map ─────────────────────────────────────────────────────────────────
//
// Maps a FlatRouteKey → the absolute URL path used in <Link to=...>,
// navigate(), and redirects.
// e.g. routePaths.engagementLetterDatabaseClient === '/engagement-letters/client/:clientId'

export const routePaths: Record<FlatRouteKey, string> = {
  root: safeRoutes?.root?.path ?? fallbackRoutePaths.root,
  engagementLetterDatabase: safeRoutes?.engagementLetterDatabase?.path ?? fallbackRoutePaths.engagementLetterDatabase,
  engagementLetterDatabaseHome:
    safeRoutes?.engagementLetterDatabase?.home?.path ?? fallbackRoutePaths.engagementLetterDatabaseHome,
  engagementLetterDatabaseClient:
    safeRoutes?.engagementLetterDatabase?.client?.path ?? fallbackRoutePaths.engagementLetterDatabaseClient,
  engagementLetterDatabaseWaiver:
    safeRoutes?.engagementLetterDatabase?.waiver?.path ?? fallbackRoutePaths.engagementLetterDatabaseWaiver,
  app: safeRoutes?.app?.path ?? fallbackRoutePaths.app,
};

// ─── Convenience type aliases ─────────────────────────────────────────────────
//
// These are the aliases used by app-level consumers (EngagementLetterDatabaseRoutes,
// NotFound, etc.).  They mirror what used to live in routes/paths/paths.ts.

export const AppRouteSegment = routeSegments;
export type AppRouteSegment = (typeof routeSegments)[FlatRouteKey];

export const AppRoutePath = routePaths;
export type AppRoutePath = (typeof routePaths)[FlatRouteKey];
